import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Filter } from 'lucide-react';
import PageWrapper from '@/components/layout/PageWrapper';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import StatusBadge from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import RequestForm from '@/components/repairRequests/RequestForm';
import CompanySelect from '@/components/shared/CompanySelect';
import { repairRequestsApi } from '@/api';
import { formatDate, truncate } from '@/lib/utils';
import type { RepairRequest } from '@/types';

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All — Not Done', value: 'not_done' },
  { label: 'All Statuses', value: 'all' },
  { label: 'New', value: 'New' },
  { label: 'In Progress', value: 'In-Progress' },
  { label: 'Parts Ordered', value: 'Parts Ordered' },
  { label: 'Done', value: 'Done' },
];

export default function RepairRequests() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('not_done');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [newRequestOpen, setNewRequestOpen] = useState(() => searchParams.get('new') === '1');

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setNewRequestOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['repairRequests', statusFilter, companyFilter],
    queryFn: () =>
      repairRequestsApi.list({
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(companyFilter && companyFilter !== 'all' ? { company: companyFilter } : {}),
      }),
  });

  const columns: ColumnDef<RepairRequest>[] = [
    {
      key: 'id',
      header: 'ID',
      className: 'w-24 font-mono text-xs',
      cell: (row) => String(row.id).padStart(6, '0'),
    },
    {
      key: 'equipment',
      header: 'Equipment',
      cell: (row) => (
        <span className="font-medium">
          {row.equipment?.unitNumber ?? '—'}
        </span>
      ),
    },
    {
      key: 'problem',
      header: 'Problem',
      cell: (row) => (
        <span className="text-muted-foreground">{truncate(row.problem, 60)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'company',
      header: 'Company',
      cell: (row) => (
        <span className="rounded bg-secondary px-1.5 py-0.5 text-xs font-medium text-secondary-foreground">
          {row.equipment?.company ?? '—'}
        </span>
      ),
    },
    {
      key: 'mechanicId',
      header: 'Mechanic',
      cell: (row) => row.mechanicId
        ? <span className="text-xs text-muted-foreground">Assigned</span>
        : <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: 'requestDate',
      header: 'Date',
      sortable: true,
      cell: (row) => <span className="text-muted-foreground">{formatDate(row.requestDate)}</span>,
    },
  ];

  return (
    <PageWrapper
      title="Repair Requests"
      subtitle="Track and manage all repair work orders"
      actions={
        <Button onClick={() => setNewRequestOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Request
        </Button>
      }
    >
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <CompanySelect
          value={companyFilter}
          onValueChange={setCompanyFilter}
          includeAll
          allLabel="All Companies"
          className="w-44"
        />
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.total} result{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <DataTable<RepairRequest>
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        emptyMessage="No repair requests found."
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/repair-requests/${row.id}`)}
      />

      {/* New Request Modal */}
      <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Repair Request</DialogTitle>
          </DialogHeader>
          <RequestForm
            onSuccess={() => {
              setNewRequestOpen(false);
              refetch();
            }}
            onCancel={() => setNewRequestOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
}
